import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FiEdit2, FiTrash2, FiFilter, FiDollarSign,
  FiCheckCircle, FiXCircle, FiClock, FiCalendar
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import expenseService from '../../services/expenseService';
import { useAuth } from '../../contexts/AuthContext';
import CommonModal from '../../services/CommonModal';
import { FaFileExcel } from 'react-icons/fa';

const ExpenseList = ({ expenses, onDelete, onRefresh, projects, budgets }) => {
  const { user } = useAuth();
  const [filters, setFilters] = useState({
    project_id: '',
    budget_id: '',
    expense_type: '',
    status: '',
    start_date: '',
    end_date: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [statistics, setStatistics] = useState(null);
  const [processingExpense, setProcessingExpense] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const confirmDelete = () => {
    onDelete(selectedExpense.id);
    setShowDeleteModal(false);
    setSelectedExpense(null);
  };

  useEffect(() => {
    if (expenses && expenses.length > 0) {
      calculateStatistics();
    }
  }, [expenses]);

  const calculateStatistics = () => {
    const totalAmount = expenses.reduce((sum, expense) =>
      sum + parseFloat(expense.amount || 0), 0
    );
    const pendingAmount = expenses
      .filter(e => e.status === 'Pending')
      .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
    const approvedAmount = expenses
      .filter(e => e.status === 'Approved')
      .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);
    const paidAmount = expenses
      .filter(e => e.status === 'Paid')
      .reduce((sum, expense) => sum + parseFloat(expense.amount || 0), 0);

    setStatistics({
      totalAmount: parseFloat(totalAmount.toFixed(2)),
      pendingAmount: parseFloat(pendingAmount.toFixed(2)),
      approvedAmount: parseFloat(approvedAmount.toFixed(2)),
      paidAmount: parseFloat(paidAmount.toFixed(2)),
      totalExpenses: expenses.length,
      pendingCount: expenses.filter(e => e.status === 'Pending').length
    });
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    onRefresh(filters);
    setShowFilters(false);
  };

  const clearFilters = () => {
    const emptyFilters = {
      project_id: '',
      budget_id: '',
      expense_type: '',
      status: '',
      start_date: '',
      end_date: ''
    };
    setFilters(emptyFilters);
    onRefresh(emptyFilters);
  };

  const handleDelete = async (id, description) => {
    {
      try {
        await onDelete(id);
      } catch (error) {
        console.error('Error deleting expense:', error);
      }
    }
  };

  const handleApprove = async (expenseId) => {
    if (!window.confirm('Are you sure you want to approve this expense?')) {
      return;
    }

    setProcessingExpense(expenseId);
    try {
      await expenseService.approveExpense(expenseId, user.id);
      toast.success('Expense approved successfully');
      onRefresh(filters);
    } catch (error) {
      console.error('Error approving expense:', error);
      toast.error(error.response?.data?.error || 'Failed to approve expense');
    } finally {
      setProcessingExpense(null);
    }
  };

  const handleReject = async (expenseId) => {
    if (!window.confirm('Are you sure you want to reject this expense?')) {
      return;
    }

    setProcessingExpense(expenseId);
    try {
      await expenseService.rejectExpense(expenseId, user.id);
      toast.success('Expense rejected');
      onRefresh(filters);
    } catch (error) {
      console.error('Error rejecting expense:', error);
      toast.error(error.response?.data?.error || 'Failed to reject expense');
    } finally {
      setProcessingExpense(null);
    }
  };

  const applyDatePreset = (preset) => {
    const presets = expenseService.getDateRangePresets();
    if (presets[preset]) {
      setFilters(prev => ({
        ...prev,
        start_date: presets[preset].start_date,
        end_date: presets[preset].end_date
      }));
    }
  };

  const handleDownloadExpenseList = () => {
    if (!expenses || expenses.length === 0) {
      toast.error('No expenses to download');
      return;
    }

    const headers = [
      'Date', 'Description', 'Type', 'Project', 'Amount', 'Status'
    ];

    const escapeCsv = (value) => {
      if (value === null || value === undefined) return '';
      const str = String(value);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    };

    const rows = expenses.map((expense) => [
      expenseService.formatDate(expense.expense_date),
      expense.description,
      expense.expense_type,
      expense.project?.name || 'N/A',
      expense.amount,
      expense.status
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map(escapeCsv).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `expenses-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success('Expense list downloaded');
  };

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      {statistics && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Expenses</p>
                <p className="text-2xl font-bold text-gray-900">
                  {expenseService.formatCurrency(statistics.totalAmount)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {statistics.totalExpenses} expenses
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FiDollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
          <CommonModal
            isOpen={showDeleteModal}
            title="Delete Expense"
            message="Are you sure you want to delete this expense?"
            onClose={() => {
              setShowDeleteModal(false);
              setSelectedExpense(null);
            }}
            onConfirm={confirmDelete}
            type="error"
            confirmText="Delete"
            cancelText="Cancel"
          />
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">
                  {expenseService.formatCurrency(statistics.pendingAmount)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {statistics.pendingCount} pending
                </p>
              </div>
              <div className="p-3 badge-warning rounded-full">
                <FiClock className="w-6 h-6" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-green-600">
                  {expenseService.formatCurrency(statistics.approvedAmount)}
                </p>
              </div>
              <div className="p-3 badge-success rounded-full">
                <FiCheckCircle className="w-6 h-6 " />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Paid</p>
                <p className="text-2xl font-bold text-blue-600">
                  {expenseService.formatCurrency(statistics.paidAmount)}
                </p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <FiCheckCircle className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center text-gray-700 hover:text-gray-900"
          >
            <FiFilter className="mr-2" />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>
          <button
            onClick={handleDownloadExpenseList}
            className="px-4 py-2 bg-white border border-green-600 text-green-600 rounded-lg flex items-center gap-2"
            title="Download expense list"
          >
            <FaFileExcel className="mr-2" />
            Download
          </button>
        </div>

        {showFilters && (
          <div className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Project
                </label>
                <select
                  name="project_id"
                  value={filters.project_id}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Projects</option>
                  {projects?.map(project => (
                    <option key={project.id} value={project.id}>
                      {project.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Budget
                </label>
                <select
                  name="budget_id"
                  value={filters.budget_id}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Budgets</option>
                  {budgets?.map(budget => (
                    <option key={budget.id} value={budget.id}>
                      {budget.category} - {budget.project?.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expense Type
                </label>
                <select
                  name="expense_type"
                  value={filters.expense_type}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Types</option>
                  {expenseService.getExpenseTypes().map(type => (
                    <option key={type.value} value={type.value}>
                      {type.icon} {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">All Statuses</option>
                  {expenseService.getStatuses().map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Date
                </label>
                <input
                  type="date"
                  name="start_date"
                  value={filters.start_date}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Date
                </label>
                <input
                  type="date"
                  name="end_date"
                  value={filters.end_date}
                  onChange={handleFilterChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Date Presets */}
            <div className="flex flex-wrap gap-2">
              <span className="text-sm text-gray-600 mr-2">Quick filters:</span>
              <button
                type="button"
                onClick={() => applyDatePreset('this-month')}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                This Month
              </button>
              <button
                type="button"
                onClick={() => applyDatePreset('last-month')}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Last Month
              </button>
              <button
                type="button"
                onClick={() => applyDatePreset('this-year')}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                This Year
              </button>
              <button
                type="button"
                onClick={() => applyDatePreset('last-30-days')}
                className="px-3 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
              >
                Last 30 Days
              </button>
            </div>

            <div className="flex justify-end space-x-2">
              <button
                onClick={clearFilters}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Clear
              </button>
              <button
                onClick={applyFilters}
                className="px-4 py-2 text-white bg-blue-600 rounded-md hover:bg-blue-700"
              >
                Apply Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Expense List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {expenses && expenses.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {expenses.map((expense) => (
                  <tr key={expense.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="flex items-center">
                        <FiCalendar className="mr-2 text-gray-400" />
                        {expenseService.formatDate(expense.expense_date)}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">
                        {expense.description}
                      </div>
                      {expense.receipt_url && (
                        <a
                          href={expense.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View Receipt
                        </a>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${expenseService.getTypeColor(expense.expense_type)}-100 text-${expenseService.getTypeColor(expense.expense_type)}-800`}>
                        {expenseService.getTypeIcon(expense.expense_type)} {expense.expense_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {expense.project?.name || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {expenseService.formatCurrency(expense.amount, expense.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${expenseService.getStatusBadgeClass(expense.status)}`}>
                        {expense.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        {expense.status === 'Pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(expense.id)}
                              disabled={processingExpense === expense.id}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50"
                              title="Approve"
                            >
                              <FiCheckCircle className="inline w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleReject(expense.id)}
                              disabled={processingExpense === expense.id}
                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                              title="Reject"
                            >
                              <FiXCircle className="inline w-4 h-4" />
                            </button>
                          </>
                        )}
                        <Link
                          to={`/app/expenses/${expense.id}/edit`}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <FiEdit2 className="inline w-4 h-4" />
                        </Link>
                        <button
                          onClick={() => {
                            setSelectedExpense(expense);
                            setShowDeleteModal(true);
                          }}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FiTrash2 className="inline w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <FiDollarSign className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No expenses found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating a new expense.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ExpenseList;

